// frontend/admin-portal/src/pages/TreasuryBabylon.jsx
import React, { useEffect, useState } from "react";
import { PageHeader, Card, Spinner, Alert, Badge } from "@fiberhome/shared-ui";
import { formatMoney, formatPercent } from "@fiberhome/shared-ui";
import { treasuryService } from "../services/treasuryService";
import { useSettings } from "../contexts/SettingsContext";

const BUCKET_LABELS = {
  operating: "Operating (70% rule)",
  reinvest: "Reinvest (20% rule)",
  reserve: "Reserve",
  ownerDraw: "Owner Draw (10% rule)",
  tax: "Tax",
  other: "Other",
};

const BUCKET_COLORS = {
  operating: "#198754",
  reinvest: "#0d6efd",
  reserve: "#0dcaf0",
  ownerDraw: "#6610f2",
  tax: "#6c757d",
  other: "#adb5bd",
};

export default function TreasuryBabylon() {
  const { get } = useSettings();
  const settingsCurrency = get(
    "finance.baseCurrency",
    get("finance.currencySymbol", "IQD"),
  );

  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    treasuryService
      .babylon(days)
      .then((d) => alive && setData(d))
      .catch(
        (err) => alive && setError(err.response?.data?.message || err.message),
      )
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [days]);

  // Display currency: backend baseCurrency (honors settings) > settings > USD
  const currency =
    data?.currency || data?.baseCurrency || settingsCurrency || "USD";

  if (loading) {
    return (
      <>
        <PageHeader title="Babylon Dashboard" />
        <Card>
          <div className="fh-settings-loading">
            <Spinner size="lg" />
          </div>
        </Card>
      </>
    );
  }

  if (error) {
    return (
      <>
        <PageHeader title="Babylon Dashboard" />
        <Alert variant="danger">{error}</Alert>
      </>
    );
  }

  const totalIncome = data?.totals?.income || 0;

  return (
    <>
      <PageHeader
        title="Babylon Dashboard"
        subtitle="How your income is being allocated — based on 'The Richest Man in Babylon'"
        breadcrumbs={["Home", "Treasury", "Babylon"]}
        actions={
          <select
            className="fh-input fh-input--compact"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last year</option>
          </select>
        }
      />

      <div className="row g-3 mb-4">
        <div className="col-md-4">
          <Card hoverable>
            <div className="text-muted" style={{ fontSize: 12 }}>
              Income ({currency})
            </div>
            <h2 className="mb-0" style={{ color: "var(--fh-success)" }}>
              {formatMoney(data?.totals?.income, { symbol: currency })}
            </h2>
          </Card>
        </div>
        <div className="col-md-4">
          <Card hoverable>
            <div className="text-muted" style={{ fontSize: 12 }}>
              Outflow ({currency})
            </div>
            <h2 className="mb-0" style={{ color: "var(--fh-danger)" }}>
              {formatMoney(data?.totals?.out, { symbol: currency })}
            </h2>
          </Card>
        </div>
        <div className="col-md-4">
          <Card hoverable>
            <div className="text-muted" style={{ fontSize: 12 }}>
              Net ({currency})
            </div>
            <h2
              className="mb-0"
              style={{
                color:
                  (data?.totals?.net || 0) >= 0
                    ? "var(--fh-success)"
                    : "var(--fh-danger)",
              }}
            >
              {formatMoney(data?.totals?.net, { symbol: currency })}
            </h2>
          </Card>
        </div>
      </div>

      <Card title="The Babylon Rules" className="mb-3">
        {totalIncome === 0 && (
          <Alert variant="info" className="mb-3">
            No income recorded in this period, so the 70/20/10 split has nothing
            to compare against. Once you record Sales Revenue transactions, the
            bars below will show how close you are to the Babylon principles.
          </Alert>
        )}

        <div className="fh-babylon-rules">
          {Object.entries(BUCKET_LABELS).map(([key, label]) => {
            if (key === "other" || key === "tax") return null;

            const actual = data?.buckets?.[key] || 0;
            const suggested = data?.suggested?.[key] || 0;
            const actualPct =
              totalIncome > 0 ? (actual / totalIncome) * 100 : 0;
            const suggestedPct =
              totalIncome > 0 ? (suggested / totalIncome) * 100 : 0;
            const diff = actualPct - suggestedPct;

            return (
              <div key={key} className="fh-babylon-rule">
                <div className="fh-babylon-rule__header">
                  <div className="d-flex align-items-center gap-2">
                    <span
                      className="fh-babylon-rule__dot"
                      style={{ background: BUCKET_COLORS[key] }}
                    />
                    <strong>{label}</strong>
                  </div>
                  <div style={{ fontSize: 13 }}>
                    <strong>{formatMoney(actual, { symbol: currency })}</strong>
                    <span className="text-muted">
                      {" "}
                      ({formatPercent(actualPct)})
                    </span>
                  </div>
                </div>

                <div className="fh-babylon-rule__bar">
                  <div
                    className="fh-babylon-rule__fill"
                    style={{
                      width: `${Math.min(100, actualPct)}%`,
                      background: BUCKET_COLORS[key],
                    }}
                  />
                  <div
                    className="fh-babylon-rule__marker"
                    style={{ left: `${Math.min(100, suggestedPct)}%` }}
                    title={`Babylon suggests ${suggestedPct.toFixed(0)}%`}
                  />
                </div>

                <div className="fh-babylon-rule__footer">
                  <span className="text-muted">
                    Suggested: {formatPercent(suggestedPct)}
                  </span>
                  <Badge
                    variant={
                      Math.abs(diff) < 5
                        ? "success"
                        : diff > 0
                          ? "warning"
                          : "danger"
                    }
                  >
                    {diff >= 0 ? "+" : ""}
                    {diff.toFixed(1)}%
                  </Badge>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <Card title="Income by Category">
        {(!data?.actualIncomeByCategory ||
          data.actualIncomeByCategory.length === 0) && (
          <div className="text-muted">
            No income transactions in this period.
          </div>
        )}

        {data?.actualIncomeByCategory?.map((row) => {
          const pct = totalIncome > 0 ? (row.total / totalIncome) * 100 : 0;
          return (
            <div key={row.categoryId} className="fh-babylon-income-row">
              <div>
                <strong>{row.name}</strong>
                <div className="text-muted" style={{ fontSize: 11.5 }}>
                  Bucket: {row.bucket || "other"}
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div>{formatMoney(row.total, { symbol: currency })}</div>
                <div className="text-muted" style={{ fontSize: 11.5 }}>
                  {formatPercent(pct)}
                </div>
              </div>
            </div>
          );
        })}
      </Card>
    </>
  );
}
